// Libraries
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, inject, Input, OnChanges, OnDestroy, OnInit, Signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addMinutes, addSeconds } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { EventService } from 'src/app/services/api/events/event.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';

// Interfaces
import { Project } from 'src/app/interfaces/api/project';
import { Role } from 'src/app/interfaces/api/role';
import { User } from 'src/app/interfaces/api/user';
import { ClassifyTag } from 'src/app/interfaces/shared/classifyTag';
import { ModuleType } from 'src/app/interfaces/shared/module';
import { Tag } from 'src/app/interfaces/shared/tag';

// Classes
import { CraneCycle } from 'src/app/classes/gallery/craneCycle';
import { GalleryTool } from 'src/app/classes/gallery/gallery.tools';
import { Tools } from 'src/app/classes/shared/tools';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// UI
import { SearchSelectComponent } from 'projects/ui-library/src/lib/components/search-select/search-select.component';
import { ButtonComponent } from 'ui-library';

// Components
import { PredictionTagsComponent } from 'src/app/components/shared/gallery/prediction-tags/prediction-tags.component';

export interface DialogData {
  id: string;
  index: number;
  prediction: any;
  concreting: any;
  action: any;
  craneCycle: any;
  startDate: any;
  endDate: any;
  urls: any[];
  url: string;
  tags: Record<ModuleType, any[]>;
  customVisionIds: Record<"crane" | "risk" | "worker" | "concreting" | "action" | "zone", string>;
  metric: any;
  updated: boolean;
  risk: any;
  tracker: any;
  trackers?: any[];
  event: any;
  onUpdate?: EventEmitter<any>;
  processed?: boolean;
  disableArrow?: boolean;
  tagNames?: any;
  user?: any;
  project?: any;
  type?: string;
  name?: string;
  tagDisplayConfig?: TagDisplayConfig;
  display?: string[];
}

export type RoleName = Role['name'];
export const ALL_ROLES: RoleName[] = ['dev', 'admin', 'userAdmin', 'user', 'guest'];

/** Per tag-project visibility. A project absent from this map is never shown in this gallery instance.
 *  `ai`/`manual` list the roles allowed to see that flavor of tag for the project; an absent key means
 *  nobody sees it. Use ALL_ROLES to open a flavor to everyone. */
export type TagVisibility = Partial<Record<ModuleType, { ai?: RoleName[]; manual?: RoleName[] }>>;

export interface TagDisplayConfig {
  visibility: TagVisibility;
}

@Component({
  selector: 'app-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TimeFormatPipe,
    ButtonComponent,
    MatFormFieldModule,
    MatSelectModule,
    SearchSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})

export class GalleryComponent implements OnInit, OnDestroy, OnChanges {
  // Services
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly eventService: EventService = inject(EventService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly metricService: MetricService = inject(MetricService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly trackService: TrackService = inject(TrackService);
  private readonly tools: Tools = inject(Tools);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly galleryTool: GalleryTool = inject(GalleryTool)

  public dialogRef: MatDialogRef<GalleryComponent> | null = inject(MatDialogRef<GalleryComponent>, { optional: true });
  public _data: DialogData | null = inject(MAT_DIALOG_DATA, { optional: true });
  public readonly dialog: MatDialog = inject(MatDialog);

  // Components API
  @ViewChild("boxDrawer", { static: false }) boxDrawer!: ElementRef;

  @Input() id!: string;
  @Input() index!: number;
  @Input() prediction: any;
  @Input() craneCycle: any;
  @Input() concreting: any;
  @Input() action: any;
  @Input() startDate: any;
  @Input() endDate: any;
  @Input() urls!: any[];
  @Input() url!: string;
  @Input() tags!: Record<ModuleType, any[]>;
  @Input() metric: any;
  @Input() updated!: boolean;
  @Input() risk: any;
  @Input() tracker: any;
  @Input() event: any;
  @Input() isDialog: boolean = false;
  @Input() maxImageHeight?: string;
  @Input() processed!: boolean;
  @Input() disableArrow: boolean = false;
  @Input() craneTags: any[] = [];
  @Input() name!: string;
  @Input() display: string[] = [];
  @Input() tagNames: Record<ModuleType, string[]> = {
    llm: [],
    risk: [],
    crane: [],
    zone: [],
    action: [],
    worker: []
  }
  @Input() customVisionIds = {
    risk: "",
    crane: "",
    zone: "",
    action: "",
    concreting: "",
    worker: ""
  }
  @Input() user: User | null = null;
  @Input() project: Project | null = null;
  // Optional map of risk tagId (box _id) -> visual number; when set, risk boxes show a circled badge.
  @Input() riskNumbers?: Record<string, number>;
  // Names of tags that should not be shown (e.g. risks filtered out on the risks page).
  @Input() hiddenTags?: string[];
  @Input() tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES }
    }
  };

  // Variables
  data!: DialogData;
  imageInfo: any;
  currentHeight: string = "90%";
  currentWidth: string = "1000px";
  currentBox: any;
  currentBoxX = 0;
  currentBoxY = 0;
  drawerZIndex: Record<string, number> = { crane: 99, risk: 99, worker: 99 };
  mousedown = false;
  predictions: string[] = [];
  metrics: any[] = [];
  events: any[] = [];
  tagsChanges: Tag[] = [];
  craneTagNames: string[] = [];
  currentTagProject: ModuleType = "crane";
  lastTrack: any = null;
  randomStr: string = "";
  destroy$ = new Subject<void>();
  boxes: any[] = [];
  tagProjects = ["worker", "risk", "crane"];

  selectedTags: Record<string, string> = {
    concreting: "",
    action: "",
    zone: ""
  }

  classifications: any[] = [];
  classProjects = ["concreting", "action", "zone"];

  isRiskPage: boolean = false;
  worker: string[] = ["compagnon", "passant"];
  createTagString: string;
  _lastClassificationsUpdate: number = 0;
  _lastBoxesUpdate: number = 0;

  private loading: Record<string, any> = { crane: false, risk: false, worker: {}, zone: {}, action: {}, concreting: {} };
  private static sharedTagNames: any = null;

  // Signals
  readonly currentProject: Signal<Project> = this.projectService.currentProjectSignal;

  // Computeds
  get displayConfig(): TagDisplayConfig {
    return this.tagDisplayConfig;
  }

  @ViewChild('input', { static: false })
  set input(element: ElementRef<HTMLInputElement>) {
    if (element) {
      element.nativeElement.focus()
    }
  }

  @HostListener("mousedown", ['$event'])
  onMouseDown(event: MouseEvent) {
    let target = event.target as HTMLElement | null;
    let classList = target?.classList;

    if ((classList && !classList.contains("box") && !classList.contains("attachment-picture"))) return;

    if (this.isButtonDisabled(this.currentTagProject)) {
      return;
    }

    let drawerZIndex = this.getDrawerZIndex();
    this.boxDrawer.nativeElement.style.pointerEvents = "none";
    let hasInvalidName = (target as any)?.name && (target as any)?.name.includes("select");

    if (drawerZIndex != 0 && this.tagProjects.includes(this.currentTagProject) && !hasInvalidName) {
      this.predictions = this.getDisplayNames(this.currentTagProject);
      this.mousedown = true;
      let targetElement = event.target as HTMLElement | null;
      let counter = 0;

      while (targetElement && (!targetElement?.classList.contains("attachment-picture") && !targetElement.classList.contains("picture-container"))) {
        if (targetElement.classList.contains("box")) {
          targetElement = targetElement.parentElement;
          continue;
        }

        counter++;

        if (counter > 10) break;
      }

      if (targetElement) {
        const containerRect = targetElement.getBoundingClientRect();
        this.currentBoxX = event.clientX - containerRect.left;
        this.currentBoxY = event.clientY - containerRect.top;
      } else {
        this.currentBoxX = event.offsetX;
        this.currentBoxY = event.offsetY;
      }
    }
  }

  @HostListener("mousemove", ['$event'])
  onMouseMove(event: MouseEvent) {
    let drawerZIndex = this.getDrawerZIndex();

    if (this.mousedown && drawerZIndex != 0) {
      this.boxDrawer.nativeElement.style.pointerEvents = "auto";

      this.currentBox = {
        tagName: "",
        boundingBox: {
          left: this.currentBoxX,
          top: this.currentBoxY,
          width: event.offsetX - this.currentBoxX,
          height: event.offsetY - this.currentBoxY
        }
      }
    }
  }

  @HostListener("mouseup", ['$event'])
  onMouseUp(event: MouseEvent) {
    this.mousedown = false;
    if (this.currentBox

    ) {
      this.currentBox.done = true;
      this.setDrawerZIndex(0);

      if (this.tagNames[this.currentTagProject].length == 1) {
        this.currentBox.tagName = this.tagNames[this.currentTagProject][0];
        this.currentBox.displayName = this.galleryTool.getDisplayName(this.tagNames[this.currentTagProject][0] as string, this.currentTagProject);
        this.setBox();
      }
    }

    this.boxDrawer.nativeElement.style.pointerEvents = "none";
  }

  @HostListener("mouseleave", ['$event'])
  onMouseLeave(event: MouseEvent) {
    this.boxDrawer.nativeElement.style.pointerEvents = "none";
    this.mousedown = false;

    if (this.currentBox) {
      this.currentBox.done = true;
      this.setDrawerZIndex(0);
    }
  }

  @HostListener('keydown', ['$event'])
  handleKeyboardEvents(event: any) {
    if (event.key === 'Enter' && this.currentBox) {
      let value = this.currentBox.tagName;

      if (!value && this.predictions[0]) {
        value = this.predictions[0];
        this.currentBox.displayName = value;
        this.currentBox.tagName = this.galleryTool.getTagName(value, this.currentTagProject);
      }

      this.setBox();
    } else if (event.keyCode == 37 && this.isDialog) {
      this.prevImage()
    } else if (event.keyCode == 39 && this.isDialog)
      this.nextImage();
  }

  constructor() {
    if (this._data) {
      this.isDialog = true;
      this.data = { ...this._data };

      if (this.data.tracker?.id)
        this.data.tracker = this.data.tracker.id;

      this.galleryTool.getTagNames().then((tagNames) => {
        this.tagNames = tagNames;
        this.init(this.data);
      });
    } else {
      this.initDataFromInput();
    }

    this.createTagString = this.translate.instant('events.create_tag');
    this.randomStr = this.getRandParam();
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit(): void {
    if (!this.isDialog) {
      this.initDataFromInput();
      this.init(this.data);
    }

    if (this.data.tracker && this.tools.isHex(this.data.tracker) && this.isDialog)
      this.trackService.getLastPositions({ createdDate: this.startDate, trackers: [this.data.tracker] }).pipe(takeUntil(this.destroy$)).subscribe((tracks: any[]) => {
        if (tracks?.length) {
          this.lastTrack = tracks[0];
        }
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes["user"] && !this.isDialog)
      this.ngOnInit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initDataFromInput() {
    this.data = {
      id: this.id,
      index: this.index,
      prediction: this.prediction,
      craneCycle: this.craneCycle,
      concreting: this.concreting,
      action: this.action,
      startDate: this.startDate,
      endDate: this.endDate,
      urls: this.urls,
      url: this.url,
      tags: this.tags,
      metric: this.metric,
      updated: this.updated,
      risk: this.risk,
      tracker: this.tracker?.id || this.tracker,
      event: this.event,
      tagNames: this.tagNames,
      user: this.user,
      project: this.project || this.currentProject(),
      customVisionIds: this.customVisionIds,
      processed: this.processed,
      name: this.name,
      type: "Attachments",
      tagDisplayConfig: this.tagDisplayConfig
    };
  }

  async init(data: DialogData) {
    this.imageInfo = data;

    if (data.url) {
      this.url = data.url;
    } else {
      this.url = this.isDialog ? data.urls[data.index] : data.urls[0];
    }

    this.display = data.display || [];
    this.urls = data.urls;
    this.id = data.id;
    this.startDate = data.startDate;
    this.endDate = data.endDate ?? addMinutes(new Date(data.startDate), 30).toISOString();
    this.drawerZIndex = { crane: 99, risk: 99, worker: 99 };
    this.tags = data.tags || [];
    this.user = data.user;
    this.project = data.project || this.projectService.currentProjectValue;
    this.getMetrics();
    this.getRiskEvents();
    this.processed = data.processed ?? false;
    this.customVisionIds = data.customVisionIds;
    this.index = data.index;
    this.name = data.name || "";
    this.parseBoxes();
    this.parseClassifications();
    this.initDrawerZIndex(data);

    this.tagDisplayConfig = data.tagDisplayConfig ?? {
      visibility: {
        crane: { ai: ALL_ROLES, manual: ALL_ROLES },
        risk: { ai: ALL_ROLES, manual: ALL_ROLES },
        worker: { ai: ALL_ROLES, manual: ALL_ROLES },
        zone: { ai: ALL_ROLES, manual: ALL_ROLES },
        action: { ai: ALL_ROLES, manual: ALL_ROLES }
      }
    };

    if (!this.isDialog && GalleryComponent.sharedTagNames) {
      this.tagNames = { ...GalleryComponent.sharedTagNames };
    }

    this.setPredictions();
  }

  parseBoxes() {
    this.boxes = [];

    Object.keys(this.tags).forEach((project: string) => {
      if (!this.tagProjects.includes(project)) return;

      if (this.display?.length && !this.display.includes(project)) return;

      if (project == "worker")
        this.loading[project] = {}

      this.tags[project as ModuleType].forEach((p, i) => {

        if (!this.isAdmin() && this.project?.iaModels && !this.project.iaModels[project as ModuleType] && p.probability < 1) return;

        if (this.isAdmin() && p.probability < 0.3) return;
        if (project == "worker")
          this.loading[project][`${i}`] = false;

        this.boxes.push({
          ...p,
          displayName: this.galleryTool.getDisplayName(p.tagName, project as ModuleType),
          project,
          tagIndex: i
        })
      })
    })

    this._lastBoxesUpdate = 0;
    this.cdr.markForCheck();
  }

  parseClassifications() {
    this.classifications = []

    Object.keys(this.tags).forEach(project => {
      if (!this.classProjects.includes(project)) return;

      this.tags[project as ModuleType].sort((a, b) => b.probability - a.probability);

      if (this.display?.length && !this.display.includes(project)) return;

      let hasRetag = false;
      this.loading[project] = {};

      this.tags[project as ModuleType].forEach((p, i) => {
        if (project == "risk" && !this.isDev()) return;

        if (!this.isAdmin() && p.probability < 0.5) return;

        if (this.classifications.find(cl => cl.project == project && cl.probability < 1)) return;

        if (this.project?.iaModels && !this.project.iaModels[project as ModuleType] && p.probability < 1) return;

        if (p.probability == 1) {
          this.selectedTags[project] = p.name || p.tagName;
          hasRetag = true;
        }

        this.loading[project][`${i}`] = false;

        this.classifications.push({
          ...p,
          project,
          tagIndex: i
        })
      });

      if (hasRetag)
        this.classifications = this.classifications.filter(p => {
          if (p.project != project)
            return true;

          if (p.probability == 1 && p.project == project)
            return true;

          return false;
        })
      else
        this.selectedTags[project] = "";
    })

    this._lastClassificationsUpdate = 0;
    this.cdr.markForCheck();
  }

  initDrawerZIndex(data: DialogData) {
    data = data ?? this.imageInfo;
    let taggedBoxes = this.boxes.filter(p => p.probability == 1);
    let hasCraneTag = !!taggedBoxes.find(p => p.project == "crane");
    let hasRiskTag = !!taggedBoxes.find(p => p.project == "risk");
    this.drawerZIndex = { crane: hasCraneTag ? 0 : 99, risk: hasRiskTag ? 0 : 99, worker: 99 };
  }

  updateBoxes(tags: Tag[]) {
    this.tagsChanges = tags;
    this.dialogRef?.close()
  }

  async nextImage() {
    this.resetPredictionBox();
    this.init(await this.imageInfo.getImageInfo('prev'));
  }

  async prevImage() {
    this.resetPredictionBox();
    this.init(await this.imageInfo.getImageInfo('next'));
  }

  getMetrics() {
    if (!this.isDialog) {
      this.metrics = [this.data.metric];
      return;
    }

    const startDate = this.startDate || addSeconds(new Date(this.data.craneCycle?.startDate), 1).toISOString();

    const trackers = Array.from(
      new Set(
        [this.data.tracker, ...(this.data.trackers || [])]
          .map((t: any) => (typeof t === "object" ? t?.id : t))
          .filter(Boolean)
      )
    );

    const params: any = { atDate: startDate };

    if (trackers.length > 1) params.trackers = trackers.join(",");
    else if (trackers.length === 1) params.tracker = trackers[0];

    this.metricService.getMetrics(params).pipe(takeUntil(this.destroy$)).subscribe((res: any[]) => {
      this.metrics = res || [];
    });
  }

  getRiskEvents() {
    if (!this.isDialog) return;

    let startDate = this.startDate;

    this.eventService.getEvents({ atDate: startDate, trackers: [this.data.tracker] }).pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.events = res;
    })
  }

  setPredictions(project?: ModuleType) {
    project = project ?? this.currentTagProject;
    this.predictions = [this.createTagString, ...this.getDisplayNames(project)];
  }


  resetPredictionBox() {
    this.currentBox = null;
    this.initDrawerZIndex(this.imageInfo);
    this.boxDrawer.nativeElement.style.pointerEvents = "none";
  }

  setDrawerZIndex(number: number, project: ModuleType | null = null) {
    project = project ?? (this.currentTagProject);
    this.drawerZIndex[project] = number;
  }

  getDrawerZIndex(project: null | string = null) {
    project = project ?? this.currentTagProject;
    return this.drawerZIndex[project];
  }

  setPredictionChanged(event: MouseEvent) {
    let target = event.target as HTMLInputElement | null;
    this.currentBox.tagName = this.galleryTool.getTagName(target?.value as string, this.currentTagProject);
    this.currentBox.displayName = target?.value;
  }

  onPredictionChanged(tagName: string) {
    if (tagName && tagName.length) {
      this.predictions = this.getDisplayNames(this.currentTagProject).filter(t => t.toLowerCase().includes(tagName.toLowerCase()))
    }
  }

  onDescriptionChanged(description: string) {
    this.currentBox.description = description;
  }

  setBox() {
    let drawer = document.getElementById('box-drawer-' + this.id);

    if (!drawer) return;

    if (!this.currentBox.tagName) {
      this.currentBox = null;
      this.setDrawerZIndex(99);
      return;
    }

    let width = drawer.clientWidth;
    let height = drawer.clientHeight;

    if (this.boxDrawer) {
      let rect = this.boxDrawer.nativeElement.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
    }

    let box: any = {
      tagName: this.currentBox.tagName,
      displayName: this.currentBox.displayName || this.getDisplayName(this.currentBox.tagName),
      description: this.currentBox.description || "",
      probability: 1,
      boundingBox: {
        left: this.currentBox.boundingBox.left / width,
        top: this.currentBox.boundingBox.top / height,
        width: this.currentBox.boundingBox.width / width,
        height: this.currentBox.boundingBox.height / height
      }
    }

    this.currentBox = null;
    this.setDrawerZIndex(0);
    this.tags[this.currentTagProject].push({ ...box });
    this.parseBoxes();
    box.tagIndex = this.tags[this.currentTagProject].length - 1;
    this.saveBox(box, true);
  }

  classify(event: Event, train = false) {
    let target = event.target as HTMLSelectElement | null;
    let tagName = this.tagNames[this.currentTagProject].find(tagName => tagName == target?.value);

    if (!tagName) return;

    let previousTag = this.classifications.find(p => p.probability == 1 && p.project == this.currentTagProject);

    if (previousTag && previousTag.tagName == tagName) return;

    if (!this.tags[this.currentTagProject])
      this.tags[this.currentTagProject] = [];

    if (previousTag)
      this.tags[this.currentTagProject][previousTag.tagIndex] = { tagName, probability: 1 };
    else
      this.tags[this.currentTagProject].push({ tagName, probability: 1 })

    let tagIndex = previousTag ? previousTag.tagIndex : this.tags[this.currentTagProject].length - 1;
    this.setLoading(true, tagIndex);

    if (train) {
      let updates: any = { tags: this.tags };

      if (this.isDialog)
        this.data.onUpdate?.emit({ ...this.data, tags: this.tags, changedTags: this.tagsChanges, ...updates });

      this.attachmentService.setAttachment({ id: this.imageInfo.id, ...updates }).subscribe(res => {
        this.setLoading(false, tagIndex);
        this.parseClassifications();
      })
    } else {
      this.attachmentService.setAttachment({ id: this.imageInfo.id, tags: this.tags }).subscribe(res => {
        this.setLoading(false, tagIndex);
        this.parseClassifications();
      })
    }
  }

  removeClassification(selectedTag: ClassifyTag, train = true) {
    this.setLoading(true, selectedTag.tagIndex, selectedTag.project);
    this.tags[selectedTag.project as ModuleType] = this.tags[selectedTag.project as ModuleType].filter(p => p.tagName != selectedTag.tagName || p.probability < 1);

    if (train) {
      let updates: any = { tags: this.tags };

      if (this.isDialog)
        this.data.onUpdate?.emit({ ...this.data, tags: this.tags, changedTags: this.tagsChanges, ...updates });

      this.attachmentService.setAttachment({ id: this.imageInfo.id, ...updates }).subscribe(res => {
        this.parseClassifications();
        this.setLoading(false, selectedTag.tagIndex, selectedTag.project);
      })
    } else
      this.attachmentService.setAttachment({ id: this.imageInfo.id, tags: this.tags }).subscribe(res => {
        this.parseClassifications();
        this.setLoading(false, selectedTag.tagIndex, selectedTag.project);
      })
  }

  saveBox(box: any, train = false) {
    let boxes = [];
    let trainingBoxes = [];

    if (box) {
      boxes = this.tags[this.currentTagProject].filter((p, i) => {
        if (box.tagIndex >= 0)
          return box.tagIndex == i;

        return i == this.tags[this.currentTagProject].length - 1;
      });

      this.data.tags = this.tags;
    }

    this.setLoading(true, this.tags[this.currentTagProject].length - 1);
    let project = this.currentTagProject;

    if (train) {
      trainingBoxes = boxes;

      let otherRegions = this.tags[project].filter((p, i) => i != box.tagIndex && p.probability == 1);

      this.attachmentService.training({ id: this.imageInfo.id, name: this.name, url: this.url, processed: this.processed }, trainingBoxes, project, false, otherRegions).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.setLoading(false, this.tags[this.currentTagProject].length - 1);
        let updates: any = { tags: { ...this.tags } };

        if (this.worker.includes(box.tagName)) {
          this.processed = this.data.processed = updates.processed = res.processed;
        }

        if (res.path)
          updates.path = res.path;

        this.url = "";
        this.url = this.data.url = res.url;
        this.randomStr = (res.reload ? this.getRandParam() : "");



        this.attachmentService.setAttachment({ id: this.imageInfo.id, ...updates }).pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.parseBoxes();

          if (this.currentTagProject == "worker")
            this.setDrawerZIndex(99);
        })

        if (box && this.isCraneTag(box) && box.tagName != "compagnon") {
          this.updateMetrics(box);
        } else if (box && this.isRiskTag(box)) {
          if (this.events.length) {
            this.events.forEach((event, i) => {
              if (box.boundingBox && (event.type == "Risk")) {
                event.title = box.tagName;
                event.comment = box.description;
                event.displayTitle = this.galleryTool.getDisplayName(box);
                let payload: any = { id: event.id, title: box.tagName, comment: box.description, displayName: event.displayName };

                if (this.lastTrack) {
                  payload["position"] = {};
                  ["lat", "lon", "x", "y", "z"].forEach(xyz => { if (this.lastTrack[xyz]) payload["position"][xyz] = this.lastTrack[xyz]; })
                }

                this.eventService.updateEvent(payload).pipe(takeUntil(this.destroy$)).subscribe((res: any) => { })
                this.data.event = event;
              }
            })
          } else if (this.data.tracker) {
            let startDate = this.startDate;
            let endDate = this.endDate;
            let payload: any = { startDate, endDate, trackers: [this.data.tracker], color: "danger", type: "Risk", title: box.tagName, comment: box.description };

            if (this.lastTrack) {
              payload["position"] = {};
              ["lat", "lon", "x", "y", "z"].forEach(xyz => { if (this.lastTrack[xyz]) payload["position"][xyz] = this.lastTrack[xyz]; })
            }

            this.eventService.addEvent(payload).pipe(takeUntil(this.destroy$)).subscribe(res => {
              this.data.event = { ...res };
            });

            this.data.event = payload;
          }
        }

        if (this.isDialog)
          this.data.onUpdate?.emit({ ...this.data, tags: this.tags, changedTags: this.tagsChanges, ...updates });
      });
    }
  }

  removeBox(index: number) {
    let boxes = this.boxes.map(b => {
      return {
        tagName: b.tagName || b.name,
        description: b.description,
        probability: b.probability,
        boundingBox: b.boundingBox
      }
    });

    let project = this.boxes[index].project;
    let tagIndex = this.boxes[index].tagIndex;
    let deletedBoxes: any = boxes.splice(index, 1);
    let isWorker = this.worker.includes(deletedBoxes[0].tagName);
    let unpixelate = isWorker && this.processed;

    this.setLoading(true, tagIndex, project);
    let otherRegions = this.tags[project as ModuleType].filter((p, i) => i != tagIndex && p.probability == 1);

    this.attachmentService.training({ id: this.imageInfo.id, url: this.url, name: this.name, processed: this.processed, customVisionIds: this.customVisionIds }, unpixelate ? deletedBoxes : [], project, unpixelate, otherRegions).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      this.setLoading(false, tagIndex, project);
      let updates: any = {};

      if (isWorker)
        this.processed = this.data.processed = updates.processed = res.processed;

      this.url = "";
      this.url = this.data.url = res.url;
      this.randomStr = (res.reload ? this.getRandParam() : "");
      this.setDrawerZIndex(99, project);
      this.tags[project as ModuleType].splice(tagIndex, 1);
      this.parseBoxes();
      this.data.tags = updates.tags = this.tags;

      if (res.path)
        updates.path = res.path;

      this.attachmentService.setAttachment({ id: this.imageInfo.id, ...updates }).pipe(takeUntil(this.destroy$)).subscribe(res => { });

      if (project == "crane")
        this.updateMetrics();

      if (project == "risk") {
        let id = this.events.length == 1 ? this.events[0].id : this.data.event?.id;

        if (id)
          this.eventService.removeEvent(id).pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.data.event = { id, type: this.data.event?.type || "Risk", deleted: true }
          })
      }

      if (this.isDialog)
        this.data.onUpdate?.emit({ ...this.data, tags: this.tags, changedTags: this.tagsChanges, ...updates });
    });
  }

  updateMetrics(box: any | null = null) {
    this.metrics.filter(m => ["coup de grue", "crane cycle"].includes(m.name.toLowerCase())).forEach((m, i) => {
      if ((box?.boundingBox || !box)) {
        let tagName = box?.tagName || m.payload.prediction;
        m.payload.label = tagName;
        m.payload.title = tagName;
        m.payload.displayTitle = this.galleryTool.getDisplayName(tagName, "crane");
        m.payload.color = CraneCycle.getColor(tagName);
        m.payload.group = CraneCycle.getGroup(tagName);

        this.metricService.setMetric({
          id: m.id,
          updateSequences: true,
          name: m.name,
          tracker: m.tracker || this.data.tracker,
          payload: m.payload,
          createdDate: m.createdDate
        }).pipe(takeUntil(this.destroy$)).subscribe((res: any) => { })

        this.data.metric = m;
        this.data.prediction = tagName;
        this.data.updated = true;
      }
    });
  }

  setLoading(bool: boolean, index: number | null = null, project: ModuleType | null = null) {
    project = project ?? this.currentTagProject;

    if (!["worker", "zone", "action"].includes(project))
      this.loading[project] = bool;
    else {
      this.loading[project][`${index}`] = bool;
    }
  }

  getLoading(box: any, index = null) {
    let project = box.project || this.currentTagProject;

    return !["worker", "zone", "action"].includes(project) ? this.loading[project] : this.loading[project][`${index ?? box.tagIndex}`];
  }

  editTags() {
    const dialogRef = this.dialog.open(PredictionTagsComponent, {
      width: "65%",
      height: "70%",
      data: {
        update: this.updateBoxes.bind(this)
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (this.isDialog) {
        this.galleryTool.getTagNames(true).then(() => { });
      }
    })
  }

  onImgLoaded(evt: Event) {
    const img = evt.target as HTMLImageElement | null;

    if (img) {
      this.currentWidth = img.clientWidth + 'px';
      this.currentHeight = img.clientHeight + 'px';
    }
  }

  isRiskTag(box: any) {
    return this.galleryTool.isRiskTag(box);
  }

  isWorkerTag(box: any) {
    return this.galleryTool.isWorkerTag(box);
  }

  isCraneTag(box: any) {
    return this.galleryTool.isCraneTag(box);
  }

  switchProject(project: ModuleType) {
    if (this.isButtonDisabled(project)) {
      return;
    }

    if (this.currentBox)
      this.resetPredictionBox();

    this.currentTagProject = project;
  }

  round(num: number) {
    return Number(num.toFixed(2));
  }

  isAdmin() {
    return ["dev", "admin"].includes(this.user?.role?.name ?? "");
  }

  isDev() {
    return this.user?.role?.name == "dev";
  }

  getTagWidth(tag: string, probability: number, fontSize = 14) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return 0;

    ctx.font = `${fontSize}px Roboto, "Helvetica Neue", sans-serif`;
    let displayName = this.galleryTool.getDisplayName(tag, this.currentTagProject);
    const fullText = `${displayName} (${(probability * 100).toFixed()}%)`;

    const textMetrics = ctx.measureText(fullText);
    const tagWidth = textMetrics.width;

    return tagWidth;
  }

  getTagPosition(box: any): string {
    return `calc(${box.boundingBox.left * 100}% + ${box.boundingBox.width * 50}% - ${this.getTagWidth(box.tagName ? box.tagName : box.name, box.probability) / 2}px)`
  }

  getDisplayName(tag: any, project?: "crane" | "risk") {
    return this.galleryTool.getDisplayName(tag, project || this.currentTagProject);
  }

  getDisplayNames(project: ModuleType): string[] {
    return this.galleryTool.getDisplayNames(project);
  }

  getTagName(displayName: string, project?: ModuleType) {
    return this.galleryTool.getTagName(displayName, project ?? this.currentTagProject);
  }

  /** Single source of truth for "can the current user see/edit this flavor of tag for this project". */
  hasRoleAccess(project: ModuleType, kind: 'ai' | 'manual'): boolean {
    const allowedRoles = this.displayConfig.visibility[project]?.[kind] ?? [];
    return allowedRoles.includes(this.user?.role?.name as RoleName);
  }

  shouldDisplayTag(box: any): boolean {
    const isAiTag = box.probability < 1;

    if (isAiTag && !this.isAiTagInModule(box)) {
      return false;
    }

    if (!box.boundingBox) {
      return false;
    }

    if (this.hiddenTags?.length && this.hiddenTags.includes(box.tagName || box.name)) {
      return false;
    }

    if (isAiTag && !this.isAdmin() && this.project?.iaModels && this.project.iaModels[box.project as ModuleType] === false) {
      return false;
    }

    return this.hasRoleAccess(box.project, isAiTag ? 'ai' : 'manual');
  }

  isAiTagInModule(item: any): boolean {
    const moduleTagNames = this.tagNames?.[item.project as ModuleType];

    if (!moduleTagNames || !moduleTagNames.length) {
      return true;
    }

    const label = (item.tagName || item.name || "").toLowerCase();
    return moduleTagNames.some(tagName => tagName?.toLowerCase() === label);
  }

  shouldDisplayClassification(classification: any): boolean {
    const isAiTag = classification.probability < 1;

    if (isAiTag && !this.isAiTagInModule(classification)) {
      return false;
    }

    if (isAiTag && this.project?.iaModels && this.project.iaModels[classification.project as ModuleType] === false) {
      return false;
    }

    return this.hasRoleAccess(classification.project, isAiTag ? 'ai' : 'manual');
  }

  trackByBoxId(index: number, box: any): any {
    return `${box.project}-${box.tagIndex}-${box.probability}`;
  }

  trackByClassificationId(index: number, classification: any): any {
    return `${classification.project}-${classification.tagIndex}-${classification.probability}`;
  }

  isButtonDisabled(project: string): boolean {
    return !this.hasRoleAccess(project as ModuleType, 'manual');
  }

  isButtonVisible(project: string): boolean {
    if (['risk', 'worker'].includes(project) && !this.isAdmin()) {
      return false;
    }

    return true;
  }

  getRandParam() {
    return `?rand=${Math.floor(Math.random() * 1000)}`;
  }

  closeGallery() {
    this.dialogRef?.close();
  }

  onActionChanged(tagName: string) {
    this.selectedTags['action'] = tagName;
    const event = { target: { value: tagName } } as any;
    this.classify(event);
  }

  onZoneChanged(tagName: string) {
    this.selectedTags['zone'] = tagName;
    const event = { target: { value: tagName } } as any;
    this.classify(event);
  }
}
